import { Stack, Typography } from '@mui/material';
import { V2ThemeProvider } from '@onevision-resources/design-theme';
import { PublicApiKeyItem } from '@provision/entity-types';
import dayjs from 'dayjs';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { useMemo } from 'react';
import { CopyToClipboardButton } from 'src/components';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { DATE_FORMAT } from 'src/pages/Settings/users/const';
import {
  ApiKeysTableWrapper,
  MaskedKeyCell,
  NameCell,
  RevokedChip,
  RevokedText,
} from './styled';

interface ApiKeysTableProps {
  apiKeys: PublicApiKeyItem[];
  isLoading: boolean;
  onEditScopes: (apiKey: PublicApiKeyItem) => void;
  onRegenerate: (apiKey: PublicApiKeyItem) => void;
  onRevoke: (apiKey: PublicApiKeyItem) => void;
}

export const ApiKeysTable = ({
  apiKeys,
  isLoading,
  onEditScopes,
  onRegenerate,
  onRevoke,
}: ApiKeysTableProps) => {
  const columns = useMemo<MRT_ColumnDef<PublicApiKeyItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ row }) => {
          const name = row.original.name;
          return (
            <NameCell>
              {row.original.disabled ? <RevokedText>{name}</RevokedText> : name}
              {row.original.disabled ? (
                <RevokedChip
                  label={<Typography variant="overline">REVOKED</Typography>}
                  size="small"
                />
              ) : null}
            </NameCell>
          );
        },
      },
      {
        id: 'key',
        header: 'API Key',
        Cell: ({ row }) => {
          const masked = row.original.maskedApiKey;
          if (row.original.disabled) {
            return (
              <MaskedKeyCell>
                <RevokedText>{masked}</RevokedText>
              </MaskedKeyCell>
            );
          }
          return (
            <MaskedKeyCell>
              <span>{masked}</span>
              <CopyToClipboardButton
                content={row.original.apiKey}
                tooltipText="Copy API key"
                successMessage="API key copied"
              />
            </MaskedKeyCell>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        Cell: ({ row }) => dayjs(row.original.createdAt).format(DATE_FORMAT),
      },
      {
        accessorKey: 'createdBy',
        header: 'Created by',
        Cell: ({ row }) => row.original.createdBy,
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: apiKeys,
    defaultColumn: { enableSorting: false, enableColumnActions: false },
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enablePagination: false,
    enableRowActions: true,
    enableStickyHeader: true,
    muiTablePaperProps: {
      elevation: 0,
      sx: { height: '100%' },
    },
    state: { isLoading },
    initialState: { columnPinning: { right: ['mrt-row-actions'] } },
    renderRowActions: ({ row, table }) => {
      const options: EllipsesMenuOption[] = row.original.disabled
        ? [{ label: 'No actions available', disabled: true }]
        : [
            {
              label: 'Edit scopes',
              icon: 'sliders-horizontal',
              iconLibrary: 'lucide',
              testId: 'api-keys-edit-scopes',
              onClick: () => onEditScopes(row.original),
            },
            {
              label: 'Regenerate key',
              icon: 'rotate-cw',
              iconLibrary: 'lucide',
              onClick: () => onRegenerate(row.original),
            },
            {
              label: 'Revoke key',
              icon: 'ban',
              iconLibrary: 'lucide',
              danger: true,
              onClick: () => onRevoke(row.original),
            },
          ];

      return (
        <V2ThemeProvider>
          <EllipsesMenu options={options} title={table.options.localization.rowActions} />
        </V2ThemeProvider>
      );
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        header: '',
        size: 56,
      },
    },
  });

  if (!apiKeys.length && !isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <Typography variant="body2" color="text.secondary">
          No API keys match the current filters.
        </Typography>
      </Stack>
    );
  }

  return (
    <ApiKeysTableWrapper>
      <MaterialReactTable table={table} />
    </ApiKeysTableWrapper>
  );
};
